import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  dashboardService,
  type BucketBy,
  type BucketedAlumni,
  type ConstellationDetail,
  type ConstellationPlace,
} from '../../../services/dashboard.service';
import { formatIndianNumber } from '../../../utils/currencyFormatter';
import { useConstellationDetails } from '../hooks/useConstellationDetails';
import { ConstellationTable } from './ConstellationTable';
import { AlumniQuickViewSheet } from './AlumniQuickViewSheet';
import { LocationPicker } from './LocationPicker';
import { useTerritoriesApi } from '../hooks/useTerritoriesApi';
import { DESIGNATION_COLORS, DESIGNATION_SHORT } from '../designation';
import { ResearchScheduleSheet } from './ResearchScheduleSheet';
import { CalendarCheck, Info } from '@phosphor-icons/react';
import { useSessionState } from '../../../hooks/useSessionState';

function InfoTooltip({ text, dark = false }: { text: string; dark?: boolean }) {
  return (
    <div className="group/tip relative inline-flex">
      <Info
        size={13}
        weight="bold"
        className={dark ? 'text-slate-500 hover:text-slate-700 cursor-help transition-colors' : 'text-slate-300 hover:text-slate-500 cursor-help transition-colors'}
      />
      <div className="pointer-events-none absolute z-40 top-full left-0 mt-2 w-56 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-150 origin-top-left">
        <div className="bg-slate-900 text-white text-[10px] font-medium leading-relaxed rounded-lg px-3 py-2 shadow-xl">
          {text}
        </div>
        <div className="absolute bottom-full left-3 w-2 h-2 bg-slate-900 rotate-45 -mb-1" />
      </div>
    </div>
  );
}

/**
 * Colours keyed on the EXACT sector strings stored in
 * vals_framework.business_sector. Note "TECHNOLOGY, HEALTHCARE" is a single
 * sector containing a comma, not two joined names.
 * Source of truth: Linkam_backend/app/api/v1/deepsearch/migrate_sectors_service.py
 */
const SECTOR_COLORS: Record<string, string> = {
  'MANUFACTURING/HEAVY MACHINERY/METALLURGY': '#6366f1',
  'FMCG, CONSUMER GOODS AND RETAIL': '#f59e0b',
  'FINANCE, BANKING AND VENTURE CAPITAL': '#10b981',
  'ENTERTAINMENT, MEDIA & SPORTS': '#ec4899',
  'TECHNOLOGY, HEALTHCARE': '#3b82f6',
  'STRATEGY CONSULTING & CORPORATE ADVISORY': '#8b5cf6',
  'SOFTWARE, SAAS & CONSUMER TECH PLATFORM': '#06b6d4',
  'PUBLIC POLICY, GOVERNANCE & SOCIAL IMPACT': '#eab308',
  'INFRASTRUCTURE, CIVIL ENGG': '#a855f7',
  'SEMICONDUCTORS, ELECTRONICS & ELECTRICAL EQUIPMENT': '#0ea5e9',
  'AUTOMOTIVE & MOBILITY': '#14b8a6',
  'ENERGY, OIL & GAS & RENEWABLE ENERGY': '#f97316',
  'AEROSPACE, DEFENCE & SPACE': '#64748b',
  'PHARMA, BIOTECH & MEDICAL DEVICES': '#22c55e',
  'CHEMICALS & SPECIALTY MATERIALS': '#e11d48',
  'CLEAN TECH, CLIMATE & ENVIRONMENT': '#059669',
  'EDUCATION & EDTECH': '#7c3aed',
  'AGRICULTURE, AGRITECH & FOOD PROCESSING': '#65a30d',
  'ARCHITECTURE, DESIGN & CREATIVE SERVICES': '#d946ef',
  'NON-PROFIT, PHILANTHROPY & DEVELOPMENT ORGANIZATIONS': '#0d9488',
  OTHER: '#94a3b8',
};

/** Sidebar labels. The full sector strings are far too long for a filter row. */
const SECTOR_SHORT: Record<string, string> = {
  'MANUFACTURING/HEAVY MACHINERY/METALLURGY': 'Manufacturing',
  'FMCG, CONSUMER GOODS AND RETAIL': 'FMCG & Retail',
  'FINANCE, BANKING AND VENTURE CAPITAL': 'Finance & VC',
  'ENTERTAINMENT, MEDIA & SPORTS': 'Media & Sports',
  'TECHNOLOGY, HEALTHCARE': 'Tech & Healthcare',
  'STRATEGY CONSULTING & CORPORATE ADVISORY': 'Consulting',
  'SOFTWARE, SAAS & CONSUMER TECH PLATFORM': 'Software & SaaS',
  'PUBLIC POLICY, GOVERNANCE & SOCIAL IMPACT': 'Public Policy',
  'INFRASTRUCTURE, CIVIL ENGG': 'Infrastructure',
  'SEMICONDUCTORS, ELECTRONICS & ELECTRICAL EQUIPMENT': 'Semiconductors',
  'AUTOMOTIVE & MOBILITY': 'Automotive',
  'ENERGY, OIL & GAS & RENEWABLE ENERGY': 'Energy',
  'AEROSPACE, DEFENCE & SPACE': 'Aerospace',
  'PHARMA, BIOTECH & MEDICAL DEVICES': 'Pharma & Biotech',
  'CHEMICALS & SPECIALTY MATERIALS': 'Chemicals',
  'CLEAN TECH, CLIMATE & ENVIRONMENT': 'Clean Tech',
  'EDUCATION & EDTECH': 'Education',
  'AGRICULTURE, AGRITECH & FOOD PROCESSING': 'Agriculture',
  'ARCHITECTURE, DESIGN & CREATIVE SERVICES': 'Architecture',
  'NON-PROFIT, PHILANTHROPY & DEVELOPMENT ORGANIZATIONS': 'Non-Profit',
  OTHER: 'Other',
};

/**
 * Low-to-high gradient so ask ranges read as ordered, not categorical.
 * Exactly as long as the backend's ABSOLUTE_MAX_BUCKETS (8) — the ceiling the
 * elastic bucket budget may grow to — so no two ranges ever share a hue by
 * wrapping. Cool for modest asks, warm for the principal-gift tiers.
 */
const ASK_RANGE_COLORS = [
  '#a5f3fc',
  '#67e8f9',
  '#38bdf8',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#f97316',
  '#ea580c',
];

/**
 * Distance gradient, near -> far: one hue, dark -> light, so "closer" reads as
 * "stronger". Fed to a continuous visualMap keyed on each dot's own distance
 * in km. Validated as a ramp (monotone lightness, visible steps, light end
 * >= 2:1 on white) with the dataviz validator.
 */
const DISTANCE_COLORS = ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef'];
/**
 * People in the selected city itself, as opposed to merely inside the radius.
 * A hue away from the distance ramp rather than another step of it, because it
 * answers a different question ("here" vs "how far"). Checked against the ramp
 * with the dataviz validator: ΔE 24.7 under protanopia, 33.6 for normal vision.
 */
const AT_PLACE_COLOR = '#eb6834';
/** A state or country is a yes/no, so its group is one solid step. */
const INSIDE_COLOR = '#1c5cab';
/** The swatch standing in for the whole gradient in the sidebar and the list. */
const GRADIENT_SWATCH = '#2a78d6';

const DEFAULT_PLACE_ID = 'city:1264527'; // Chennai
const DEFAULT_RADIUS_KM = 500;

/**
 * [x, y, indexIntoAlumniRef, distanceKm] — never the full alumnus object.
 * The fourth slot is what the distance gradient reads; it is -1 when the API
 * sent no distance (a region-only location, or a state/country selection).
 */
type Point = [number, number, number, number];

const FALLBACK_COLOR = '#94a3b8';

/**
 * A city sends two groups, [at the city, within the radius]; a state or country
 * sends one. Nobody outside the selection is plotted at all.
 */
const locationColor = (index: number, isCity: boolean): string => {
  if (!isCity) return INSIDE_COLOR;
  return index === 0 ? AT_PLACE_COLOR : GRADIENT_SWATCH;
};

/** Stable reference: a hidden series must not look like changed data. */
const EMPTY_POINTS: Point[] = [];

interface FilterItem {
  bucket: string;
  label: string;
  color: string;
  count: number;
  /** This group's share of total target-ask rupees, 0..1. */
  valueShare: number;
}

interface SectorConstellationChartProps {
  q?: string;
  batch?: string;
  department?: string;
  location?: string;
  industry?: string;
}

export const SectorConstellationChart = ({
  q,
  batch,
  department,
  location,
  industry,
}: SectorConstellationChartProps) => {
  // Target ask is the default view: it is the dimension the ask-range
  // bucketing was built for, and the one fundraisers open the chart to see.
  // Session-backed so opening a profile and pressing Back returns to the same view.
  const [bucketBy, setBucketBy] = useSessionState<BucketBy>('constellation:bucketBy', 'target_ask');
  const [alumni, setAlumni] = useState<BucketedAlumni[]>([]);
  const [buckets, setBuckets] = useState<string[]>([]);
  const [hiddenBuckets, setHiddenBuckets] = useSessionState<string[]>('constellation:hidden', []);
  const hidden = useMemo(() => new Set(hiddenBuckets), [hiddenBuckets]);

  const setHidden = useCallback(
    (action: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setHiddenBuckets((prev) => {
        const prevSet = new Set(prev);
        const nextSet = typeof action === 'function' ? action(prevSet) : action;
        return Array.from(nextSet);
      });
    },
    [setHiddenBuckets],
  );

  // Track dimension changes so switching dimensions clears hidden selections,
  // but remounting the same dimension restores them.
  const prevBucketByRef = useRef(bucketBy);

  // Which axes the wheel zooms. Feeds zoomOnMouseWheel on the two inside
  // dataZoom components, so independent zooming is a setting rather than a
  // modifier key you must hold.
  const [axisMode, setAxisMode] = useSessionState<'xy' | 'x' | 'y'>('constellation:axisMode', 'xy');
  // Location view. Place and radius persist across dimension switches so
  // coming back to Location returns to the same picture.
  const [territoryId, setTerritoryId] = useSessionState<string>('constellation:territoryId', '');
  const [placeId, setPlaceId] = useSessionState<string>('constellation:placeId', DEFAULT_PLACE_ID);
  const [radiusKm, setRadiusKm] = useSessionState<number>('constellation:radiusKm', DEFAULT_RADIUS_KM);
  const [minRadius, setMinRadius] = useState(10);
  const [maxRadius, setMaxRadius] = useState(500);
  const [selectedPlaceName, setSelectedPlaceName] = useState<string | undefined>();
  const [places, setPlaces] = useState<ConstellationPlace[]>([]);
  const territoriesApi = useTerritoriesApi();
  // Admins schedule nightly AI research per designation bucket from here.
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [placesSettled, setPlacesSettled] = useState(false);
  const placesRequested = useRef(false);
  // Derived rather than stored: react-hooks v7 rejects a synchronous setState
  // at the top of an effect.
  const placesLoading = bucketBy === 'location' && !placesSettled;
  // A city is measured in km, so the people inside its radius take the
  // gradient; a state or country is a yes/no and takes one flat colour.
  const cityCentre = bucketBy === 'location' && placeId.startsWith('city:');
  // Who the current selection leaves off the chart, straight from the API.
  const [excluded, setExcluded] = useState<{ outside: number; no_location: number } | null>(null);
  /**
   * Target ask across everyone the Directory filters match, including the
   * people the Location view leaves off the chart. Kept so a group's ₹ share
   * means the same here as under Sector or Target Ask, where every alumnus is
   * plotted and the denominator is simply what is on screen.
   */
  const [totalAsk, setTotalAsk] = useState<number | null>(null);
  /** Designation view: how many plotted alumni have no category yet. */
  const [unclassified, setUnclassified] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tooltips read from here by index, so the ECharts option never holds
  // 25k alumni objects.
  const alumniRef = useRef<BucketedAlumni[]>([]);
  // Donor whose profile sheet is open (from a dot or a list row); null when closed.
  // Session-backed so opening full profile and returning keeps the side panel open.
  const [openDonorId, setOpenDonorId] = useSessionState<string | null>('constellation:openDonorId', null);

  // Names, employers and giving for the rows actually being looked at: hovered
  // dots one at a time, the list's window fifty at a time. Shared between the
  // two, so a name fetched for a tooltip is already there when the list
  // scrolls onto it, and vice versa.
  const details = useConstellationDetails();

  // The tooltip reaches details through a ref so the option memo below need not
  // list them as a dependency. Rebuilding that option every time a name landed
  // would have ECharts replace the chart rather than merge into it, and the
  // user's zoom and pan would reset on every arriving batch.
  //
  // Updating it a commit late is harmless: both methods read the hook's own
  // internal cache ref, so even a stale copy of `details` returns current data.
  const detailsRef = useRef(details);
  useEffect(() => {
    detailsRef.current = details;
  }, [details]);

  // Zoom and pan are ECharts' own `inside` dataZoom — see the dataZoom block
  // in the option below.
  const chartRef = useRef<ReactECharts | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    // 300ms to match what the Directory table already debounces by
    // (useAlumni.ts). The filters here are driven straight off the search box,
    // so without a debounce every keystroke fired a full constellation
    // request. Aborting matters as much as the delay: the previous version
    // only discarded the RESPONSE, so typing a six-letter name left six
    // multi-megabyte transfers running and competing for the same link.
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);

      dashboardService
        .getBucketedConstellation(
          {
            bucket_by: bucketBy,
            q: q?.trim() || undefined,
            batch: batch?.trim() || undefined,
            department: department?.trim() || undefined,
            location: location?.trim() || undefined,
            industry: industry?.trim() || undefined,
            ...(bucketBy === 'location' ? { place: placeId, radius_km: radiusKm, territory_id: territoryId || undefined } : {}),
          },
          controller.signal,
        )
        .then((res) => {
          if (cancelled) return;
          const rows = res.data?.alumni ?? [];
          alumniRef.current = rows;
          setAlumni(rows);
          setBuckets(res.data?.buckets ?? []);
          setSelectedPlaceName(res.data?.bucket_meta?.place?.name);
          setExcluded(res.data?.bucket_meta?.excluded ?? null);
          setTotalAsk(res.data?.bucket_meta?.total_ask ?? null);
          setUnclassified(res.data?.bucket_meta?.pending ?? null);
          // Switching dimension invalidates any previous selection.
          if (prevBucketByRef.current !== bucketBy) {
            prevBucketByRef.current = bucketBy;
            setHidden(new Set());
          }
        })
        .catch((err) => {
          // An abort is this effect superseding itself, not a failure.
          if (cancelled || controller.signal.aborted) return;
          console.error('Failed to load bucketed constellation', err);
          setError(err?.message || 'Could not load the chart.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [bucketBy, q, batch, department, location, industry, placeId, radiusKm]);

  // The picker's place list: fetched once, the first time Location is chosen.
  // Server-side cached for an hour, so there is nothing to refetch per filter.
  useEffect(() => {
    if (bucketBy !== 'location' || placesRequested.current) return;
    placesRequested.current = true;
    dashboardService
      .getConstellationPlaces()
      .then((res) => {
        setPlaces(res.data?.places ?? []);
        setMinRadius(res.data?.min_radius_km ?? 10);
        setMaxRadius(res.data?.max_radius_km ?? 500);
      })
      .catch((err) => console.error('Failed to load constellation places', err))
      .finally(() => setPlacesSettled(true));
  }, [bucketBy]);


  // The expensive pass: one walk over every alumnus. Deliberately kept out of
  // the option memo so toggling a filter does not redo 25k iterations.
  const grouped = useMemo(() => {
    const points = new Map<string, Point[]>();
    const rupees = new Map<string, number>();
    let total = 0;
    alumni.forEach((item, i) => {
      const point: Point = [item.opportunity_score, item.recommended_ask, i, item.distance_km ?? -1];
      const list = points.get(item.bucket);
      if (list) list.push(point);
      else points.set(item.bucket, [point]);
      // Free: we are already walking every alumnus to build the point arrays.
      const ask = item.recommended_ask || 0;
      rupees.set(item.bucket, (rupees.get(item.bucket) ?? 0) + ask);
      total += ask;
    });
    return { points, rupees, total };
  }, [alumni]);

  // Shares are always out of every alumnus the filters match. Only the
  // Location view can plot a subset, and it sends that total with the data.
  const askDenominator = (bucketBy === 'location' ? totalAsk : null) ?? grouped.total;

  // Data, not functions — so this memo is lint-clean and cheap to recompute.
  const filterItems = useMemo<FilterItem[]>(() => {
    const ordered = buckets.filter((b) => grouped.points.has(b));
    // Defensive: surface any bucket the API sent rows for but did not list.
    grouped.points.forEach((_, b) => {
      if (!ordered.includes(b)) ordered.push(b);
    });

    return ordered.map((bucket, i) => ({
      bucket,
      label:
        bucketBy === 'sector'
          ? SECTOR_SHORT[bucket] ?? bucket
          : bucketBy === 'designation'
            ? DESIGNATION_SHORT[bucket] ?? bucket
            : bucket,
      color:
        bucketBy === 'sector'
          ? SECTOR_COLORS[bucket] ?? FALLBACK_COLOR
          : bucketBy === 'designation'
            ? DESIGNATION_COLORS[bucket] ?? FALLBACK_COLOR
            : bucketBy === 'location'
              ? locationColor(buckets.indexOf(bucket), cityCentre)
              : ASK_RANGE_COLORS[i % ASK_RANGE_COLORS.length],
      count: grouped.points.get(bucket)?.length ?? 0,
      valueShare: askDenominator > 0 ? (grouped.rupees.get(bucket) ?? 0) / askDenominator : 0,
    }));
  }, [buckets, grouped, bucketBy, cityCentre, askDenominator]);

  const visibleCount = useMemo(
    () => filterItems.reduce((n, it) => (hidden.has(it.bucket) ? n : n + it.count), 0),
    [filterItems, hidden],
  );

  // Exactly the rows the chart is plotting, so the list beside it can never
  // describe a different population than the dots do.
  const selectedRows = useMemo(
    () => (hidden.size ? alumni.filter((a) => !hidden.has(a.bucket)) : alumni),
    [alumni, hidden],
  );

  const options = useMemo(() => {
    if (!alumni.length) return {};

    const formatCurrency = (val: number) => formatIndianNumber(val, '0');
    const labelOf = new Map(filterItems.map((it) => [it.bucket, it.label]));

    // Every group always yields a series, hidden ones just carry no points.
    // Keeping the series array a constant length lets ECharts MERGE rather than
    // rebuild, which is what preserves the user's zoom/pan across a sidebar
    // click. Point arrays are reused by reference from `grouped`, so toggling
    // costs a reference swap, not a re-walk of 25k alumni.
    // Location: reversed so the people in the city itself paint last, on top
    // of the ones spread around it. The gradient below addresses its series by
    // index, so the reversal has to happen first.
    const seriesItems = bucketBy === 'location' ? [...filterItems].reverse() : filterItems;
    const gradientSeries = cityCentre
      ? seriesItems.findIndex((it) => it.bucket === buckets[1])
      : -1;
    const atPlaceBucket = cityCentre ? buckets[0] : null;
    const series = seriesItems
      .map((it, i) => ({
        name: it.label,
        type: 'scatter',
        data: hidden.has(it.bucket) ? EMPTY_POINTS : grouped.points.get(it.bucket) ?? EMPTY_POINTS,
        symbolSize: it.bucket === atPlaceBucket ? 8 : 6,
        /**
         * `large` batches a whole series into one painted path, which is why
         * 25k dots stay fast — but a batch carries ONE colour, so a visualMap
         * has nothing to tint per point. Measured: with large on, the gradient
         * series rendered flat #2a78d6. The gradient series therefore draws
         * the ordinary way (still progressive, and it only ever holds the
         * people inside the radius); every other series keeps the fast path.
         */
        large: i !== gradientSeries,
        largeThreshold: 2000,
        /**
         * The gradient series also opts out of progressive (chunked) drawing.
         * A chunked render that outlives its chart throws inside ECharts'
         * painter — seen once as an uncaught "eachLayer" error, and never
         * reproducible on demand. Drawing it in one pass removes the race, and
         * this series only holds the people inside the radius.
         */
        progressive: i === gradientSeries ? 0 : 2000,
        progressiveThreshold: 5000,
        itemStyle: { color: it.color, opacity: 0.8 },
        emphasis: { focus: 'series', itemStyle: { opacity: 1 } },
      }));

    return {
      backgroundColor: 'transparent',
      // No ECharts legend — filtering lives in the sidebar instead.
      legend: { show: false },
      /**
       * The distance gradient. Continuous rather than banded: every dot is
       * coloured by its own distance from the chosen centre, darkest at the
       * centre and fading out to the radius. It reads dimension 3 of the point
       * tuple, and applies only to the series holding the people inside it.
       */
      ...(gradientSeries >= 0
        ? {
            visualMap: {
              type: 'continuous',
              show: true,
              dimension: 3,
              seriesIndex: gradientSeries,
              min: 0,
              max: radiusKm,
              range: [0, radiusKm],
              calculable: false,
              orient: 'horizontal',
              left: 'center',
              bottom: 2,
              itemWidth: 12,
              itemHeight: 140,
              precision: 0,
              text: [`${radiusKm.toLocaleString('en-IN')} km`, 'Nearby'],
              textStyle: { color: '#94a3b8', fontSize: 10 },
              inRange: { color: DISTANCE_COLORS },
            },
          }
        : {}),
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 16,
        textStyle: { color: '#0f172a', fontFamily: 'Inter, sans-serif' },
        extraCssText:
          'border-radius: 12px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04); backdrop-filter: blur(8px);',
        /**
         * Async by way of ECharts' (params, ticket, callback) contract: the
         * half built from data already on the client — ask, opportunity,
         * group — paints immediately, and only the identity block and
         * cumulative giving wait on the per-donor fetch. A cache hit (any dot
         * hovered before) skips the round trip entirely and returns complete
         * markup synchronously.
         */
        formatter: (params: any, ticket: string, callback: (t: string, c: string) => void) => {
          const item = alumniRef.current[params.value[2]];
          if (!item) return '';

          const render = (detail?: ConstellationDetail) => {
            const ask = item.recommended_ask || 0;
            const opp = item.opportunity_score || 0;
            const pending = !detail;
            const muted = 'color: #cbd5e1;';

            const name = detail?.name ?? 'Loading…';
            // The Designation view groups by VALS's current role, so it shows
            // that role and employer, not Salesforce's, which can be years old.
            const showRole = bucketBy === 'designation';
            const designation = pending
              ? ''
              : (showRole ? detail?.role : detail?.designation) || (showRole ? 'Role unknown' : 'Alumni');
            const company = pending
              ? ''
              : (showRole ? detail?.role_company || detail?.company : detail?.company) || 'Unknown Company';
            const loc = pending ? '' : detail?.work_location || 'Unknown Loc';
            const alumBatch = pending ? '' : detail?.batch || 'Unknown Batch';

            return `
            <div style="min-width: 240px;">
              <div style="margin-bottom: 12px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px;">
                <div style="font-weight: 700; font-size: 14px; margin-bottom: 2px; ${pending ? muted : 'color: #0f172a;'}">${name}</div>
                <div style="font-size: 11px; color: #64748b; margin-bottom: 2px; min-height: 14px;">${pending ? '' : `${designation} @ ${company}`}</div>
                <div style="font-size: 11px; color: #94a3b8; min-height: 14px;">${pending ? '' : `${loc} • Batch of ${alumBatch}`}</div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr auto; gap: 8px; font-size: 12px; line-height: 1.6;">
                <div style="color: #64748b;">Cumulative Giving:</div>
                <div style="font-weight: 600; ${pending ? muted : 'color: #334155;'}">${pending ? '…' : `₹${formatCurrency(detail!.cumulative_giving || 0)}`}</div>

                <div style="color: #64748b;">Target Ask:</div>
                <div style="font-weight: 600; color: #334155;">₹${formatCurrency(ask)}</div>

                <div style="color: #64748b;">Group:</div>
                <div style="font-weight: 600; color: #334155;">${labelOf.get(item.bucket) ?? item.bucket}</div>
                ${
                  item.distance_km == null
                    ? ''
                    : `<div style="color: #64748b;">Distance:</div>
                <div style="font-weight: 600; color: #334155;">${Math.round(item.distance_km).toLocaleString('en-IN')} km</div>`
                }

                <div style="color: #3b82f6; font-weight: 600; margin-top: 4px;">Opportunity:</div>
                <div style="color: #3b82f6; font-weight: 700; margin-top: 4px;">${formatIndianNumber(opp, '0')}</div>
              </div>
              <div style="margin-top: 10px; padding-top: 8px; border-top: 1px dashed #f1f5f9; font-size: 10px; color: #94a3b8; font-family: monospace;">
                ((Ask − Giving) ÷ Ask) × Confidence = ${opp.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
          `;
          };

          const cached = detailsRef.current.get(item.donor_id);
          if (cached) return render(cached);

          detailsRef.current.load(item.donor_id).then(() => {
            // Still absent means the request itself failed; fall back to the
            // donor_id rather than leaving the tooltip reading "Loading…".
            const detail =
              detailsRef.current.get(item.donor_id) ??
              ({
                donor_id: item.donor_id,
                name: item.donor_id,
                cumulative_giving: 0,
                confidence: 0,
              } as ConstellationDetail);
            callback(ticket, render(detail));
          });
          return render(undefined);
        },
      },
      // ECharts `inside` zoom: pinch to zoom, wheel/drag to pan. The Zoom
      // H/V toggle decides which single axis the wheel zooms instead of
      // panning; in the default (Both) mode wheel always pans and only
      // pinch-to-zoom zooms both axes.
      //
      // filterMode 'none' moves the viewport without discarding out-of-window
      // points. 'empty' would blank them to NaN, and with scale:true on both
      // axes ECharts then auto-refits to whatever is left on every wheel step.
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          zoomOnMouseWheel: axisMode === 'x' || axisMode === 'xy' ? 'ctrl' : false,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
        },
        {
          type: 'inside',
          yAxisIndex: 0,
          filterMode: 'none',
          zoomOnMouseWheel: axisMode === 'y' || axisMode === 'xy' ? 'ctrl' : false,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
        },
      ],
      // The colour bar sits under the x axis, so the grid gives it room.
      grid: { left: '8%', right: '4%', bottom: gradientSeries >= 0 ? '22%' : '12%', top: '6%', containLabel: true },
      xAxis: {
        type: 'value',
        scale: true,
        name: 'Opportunity Score',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { color: '#94a3b8', fontSize: 11, fontWeight: 500 },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#f1f5f9' } },
        axisLabel: {
          show: true,
          formatter: (value: number) => formatIndianNumber(value, '0'),
          color: '#94a3b8',
          fontSize: 10,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'log',
        scale: true,
        name: 'Recommended Ask (midpoint)',
        nameLocation: 'middle',
        nameGap: 70,
        nameTextStyle: { color: '#94a3b8', fontSize: 11, fontWeight: 500 },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#f1f5f9' } },
        axisLabel: {
          show: true,
          formatter: (value: number) => `₹${formatCurrency(value)}`,
          color: '#94a3b8',
          fontSize: 10,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series,
    };
  }, [alumni, grouped, filterItems, hidden, axisMode, buckets, bucketBy, cityCentre, radiusKm]);

  const toggle = (bucket: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  };

  const allVisible = hidden.size === 0;
  const noneVisible = filterItems.length > 0 && hidden.size >= filterItems.length;

  return (
    <div className="w-full h-full min-h-[400px] flex flex-col bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden relative">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4 z-10 relative bg-white border-b border-slate-50">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-outfit font-bold text-base text-slate-900 tracking-tight">
              Alumni Constellation
            </h3>
            <InfoTooltip text="Visualizes alumni by Target Ask and Opportunity Score. Opportunity Score is an AI-generated metric indicating the likelihood of a successful ask based on past giving, target capacity, and confidence." dark={true} />
          </div>

          <p className="text-[10px] text-slate-400 font-medium mt-0.5">
            {loading
              ? 'Loading…'
              : `${visibleCount.toLocaleString('en-IN')} of ${alumni.length.toLocaleString('en-IN')} alumni`}
          </p>
          {/* The Location view plots only the people the selection covers, so
              it says plainly how many it is leaving out, and why. */}
          {!loading && !error && bucketBy === 'location' && excluded && (
            <p className="text-[10px] text-slate-400 font-medium">
              {`${excluded.outside.toLocaleString('en-IN')} elsewhere`}
              {excluded.no_location > 0
                ? ` · ${excluded.no_location.toLocaleString('en-IN')} without a location`
                : ''}
              {' — not shown'}
            </p>
          )}
          {/* Until the full VALS rerun most results carry no category yet.
              Say so, rather than let a sea of grey read as real data. */}
          {!loading && !error && bucketBy === 'designation' && !!unclassified && (
            <p className="text-[10px] text-slate-400 font-medium">
              {`${unclassified.toLocaleString('en-IN')} not yet classified — shown as Other until their VALS is refreshed`}
            </p>
          )}
          {!loading && !error && alumni.length > 0 && (
            <p className="text-[9px] text-slate-300 font-medium mt-1 leading-relaxed">
              Pinch to zoom · scroll or drag to pan
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Zoom axis. Independent horizontal / vertical zoom as a mode, so
              it does not depend on remembering a modifier key. */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
              Zoom
            </span>
            <div className="flex rounded-lg border border-slate-200/60 bg-slate-50 overflow-hidden">
              {([
                ['xy', 'Both', 'Zoom both axes'],
                ['x', 'H', 'Horizontal only — opportunity score'],
                ['y', 'V', 'Vertical only — target ask'],
              ] as const).map(([mode, label, title]) => (
                <button
                  key={mode}
                  onClick={() => setAxisMode(mode)}
                  title={title}
                  className={`px-2 py-1.5 text-[10px] font-semibold transition-colors ${
                    axisMode === mode
                      ? 'bg-accent/10 text-accent'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {bucketBy === 'designation' && isAdmin && (
            <button
              onClick={() => setScheduleOpen(true)}
              title="Re-run AI research for a bucket's alumni on a schedule"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200/60 bg-slate-50 px-2.5 py-1.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors"
            >
              <CalendarCheck size={13} weight="bold" />
              Scheduled research
            </button>
          )}

          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            Bucket by
          </span>
          <select
            value={bucketBy}
            onChange={(e) => setBucketBy(e.target.value as BucketBy)}
            className="bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-lg px-3 py-1.5 text-xs text-slate-800 font-medium transition-smooth"
          >
            <option value="sector">Sector</option>
            <option value="target_ask">Target Ask Range</option>
            <option value="location">Location</option>
            <option value="designation">Designation</option>
          </select>
        </div>
      </div>

      {bucketBy === 'location' && (
        <LocationPicker
          places={places}
          placesLoading={placesLoading}
          placeId={placeId}
          selectedName={selectedPlaceName}
          radiusKm={radiusKm}
          minRadius={minRadius}
          maxRadius={maxRadius}
          territoryId={territoryId}
          onPlaceChange={setPlaceId}
          onRadiusChange={setRadiusKm}
          onTerritoryChange={setTerritoryId}
          territoriesApi={territoriesApi}
        />
      )}

      {/* Chart + filter sidebar */}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 pl-1 pb-1">
          {error ? (
            <div className="h-full flex items-center justify-center text-xs text-slate-400">
              {error}
            </div>
          ) : loading ? (
            <div className="h-full flex items-center justify-center text-xs text-slate-400">
              Loading constellation…
            </div>
          ) : !alumni.length ? (
            <div className="h-full flex items-center justify-center text-xs text-slate-400">
              No alumni with a target ask match these filters.
            </div>
          ) : visibleCount === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-slate-400">
              Every group is hidden — pick one from the panel.
            </div>
          ) : (
            <ReactECharts
              ref={chartRef}
              option={options}
              onEvents={{
                click: (params: any) => {
                  const item = alumniRef.current[params.value?.[2]];
                  if (item?.donor_id) setOpenDonorId(item.donor_id);
                },
              }}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              /* Remount only on a dimension switch (21 sector series -> 7 ask
                 ranges is a genuine structural change). Within a dimension we
                 let ECharts merge, so zoom and pan survive sidebar clicks. */
              key={bucketBy}
            />
          )}
        </div>

        {/* Filter sidebar */}
        <aside className="w-[160px] shrink-0 border-l border-slate-100 flex flex-col bg-slate-50/40">
          <div className="px-2 py-2 border-b border-slate-100 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {bucketBy === 'sector'
                  ? 'Sectors'
                  : bucketBy === 'designation'
                    ? 'Designations'
                    : bucketBy === 'location'
                      ? 'Location'
                      : 'Ask Ranges'}
              </span>
              <span className="text-[9px] font-medium text-slate-300">people·₹</span>
            </div>
            {/* Two named actions rather than one button whose label flips.
                "Select all" and "Clear all" each say what they will do, and
                each greys out when it would do nothing — so the control never
                has to be read as the state it is currently in. */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setHidden(new Set())}
                disabled={!filterItems.length || allVisible}
                className="text-[9px] font-semibold text-accent hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
              >
                Select all
              </button>
              <span className="text-slate-200 text-[9px]">|</span>
              <button
                onClick={() => setHidden(new Set(filterItems.map((i) => i.bucket)))}
                disabled={!filterItems.length || noneVisible}
                className="text-[9px] font-semibold text-accent hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
              >
                Clear all
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto py-1">
            {filterItems.map((item) => {
              const isHidden = hidden.has(item.bucket);
              return (
                <button
                  key={item.bucket}
                  onClick={() => toggle(item.bucket)}
                  title={`${item.bucket} — click to ${isHidden ? 'show' : 'hide'}`}
                  aria-pressed={!isHidden}
                  className={`group w-full flex items-center gap-1.5 pl-2 pr-2 py-[6px] text-left
                    cursor-pointer border-l-2 transition-all duration-150
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-inset
                    ${
                      isHidden
                        ? 'border-transparent hover:border-slate-200 hover:bg-white active:bg-slate-100'
                        : 'border-transparent hover:border-slate-300 hover:bg-white active:bg-slate-100'
                    }`}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full shrink-0 border-2 transition-all duration-150
                      group-hover:scale-125 ${isHidden ? 'group-hover:bg-current' : ''}`}
                    style={{
                      backgroundColor: isHidden ? 'transparent' : item.color,
                      borderColor: item.color,
                      color: item.color,
                    }}
                  />
                  <span
                    className={`flex-1 min-w-0 truncate text-[10px] font-medium transition-colors duration-150
                      ${
                        isHidden
                          ? 'text-slate-400 line-through decoration-slate-300 group-hover:text-slate-600 group-hover:decoration-slate-400'
                          : 'text-slate-700 group-hover:text-slate-900'
                      }`}
                  >
                    {item.label}
                  </span>
                  <span
                    className={`text-[9px] tabular-nums shrink-0 transition-colors duration-150
                      ${isHidden ? 'text-slate-300' : 'text-slate-400 group-hover:text-slate-600'}`}
                  >
                    {item.count.toLocaleString('en-IN')}
                  </span>
                  {/* Share of total target-ask rupees. A tier can be tiny by
                      headcount and still be where most of the money is. */}
                  <span
                    className={`text-[9px] tabular-nums shrink-0 w-8 text-right font-semibold transition-colors duration-150
                      ${isHidden ? 'text-slate-300' : 'text-accent/70 group-hover:text-accent'}`}
                    title={`${(item.valueShare * 100).toFixed(2)}% of total target-ask value`}
                  >
                    {/* One decimal always: rounding to whole percents collapsed
                        every band below 1.5% to "1%" or "0%", and on a heavy
                        tail that is most of them. The threshold is the smallest
                        share that still renders as a non-zero 0.1%. */}
                    {item.valueShare >= 0.0005 ? `${(item.valueShare * 100).toFixed(1)}%` : '·'}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* The same rows, as a sortable list. Chart and list share one
            selection, one detail cache and one set of numbers. */}
        <ConstellationTable
          rows={selectedRows}
          bands={filterItems}
          details={details}
          loading={loading}
          showRole={bucketBy === 'designation'}
          onOpenDonor={setOpenDonorId}
        />
      </div>

      <AlumniQuickViewSheet
        donorId={openDonorId}
        onClose={() => setOpenDonorId(null)}
        onDonorChange={setOpenDonorId}
      />
      {isAdmin && (
        <ResearchScheduleSheet isOpen={scheduleOpen} onClose={() => setScheduleOpen(false)} />
      )}
    </div>
  );
};
